import { ResultsInput } from 'interfaces/calculateFeatures';
import { CellRuleValue, MinimalCellRule } from 'interfaces/cellRule';
import { CellType } from 'interfaces/cellType';
import { CalculateFeaturesParams, JobStatus, JobType } from 'interfaces/job';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import { PostProcessingActionCreated } from 'interfaces/postProcessingAction';
import {
  cloneDeep,
  every,
  filter,
  flatMap,
  forEach,
  fromPairs,
  get,
  includes,
  isEmpty,
  isEqual,
  map,
  uniq,
  values,
} from 'lodash';
import type { PanelConfigurationSource } from '.';
import {
  getSpotDetectionCountColumns,
  getSpotDetectionSetupFromInput,
} from '../../PostProcessingAndCalculateFeatures/spotDetectionInputSetup';
import { createPanelConfigurationDraft } from './PanelConfigurationEditor';

export interface RestoredCellTypingExecution extends PanelConfigurationSource {
  sourceJobId: string;
  frozenParams: CalculateFeaturesParams;
}

const resolveCellType = (shortId: string, options: CellType[]) => {
  const matches = filter(
    options,
    (option) => !option.isAbstract && (option.id === shortId || option.shortId === shortId)
  );
  if (matches.length !== 1) throw new Error('Previous run has an unknown or ambiguous cell type.');
  return matches[0].id;
};

const remainingCellLabelFilter = { 'cell.label': { $in: ['cell'] } };

export const isUndefinedCellSink = (action: object) => {
  const conditions: unknown = get(action, 'action_inputs.cell_filters.$and');
  if (!Array.isArray(conditions) || conditions.length !== 1) return false;
  return (
    get(action, 'output_name') === 'other_cell' &&
    get(action, 'action_params.action') === 'reassign' &&
    get(action, 'action_params.attribute_to_map') === 'cell.label' &&
    isEqual(conditions[0], remainingCellLabelFilter)
  );
};

export const rnaMapCellAssignmentLabel = (action: object, displayName: string) =>
  isUndefinedCellSink(action) ? `Assign remaining cells to ${displayName}` : `Assign ${displayName}`;

const parseRnaMapping = (
  action: PostProcessingActionCreated,
  columnProbeIds: Record<string, string>
): Record<string, CellRuleValue> => {
  if (
    get(action, 'action_params.action') !== 'reassign' ||
    get(action, 'action_params.attribute_to_map') !== 'cell.label'
  ) {
    throw new Error('Previous run contains a cell mapping that cannot be represented by this rule matrix.');
  }
  const conditions = get(action, 'action_inputs.cell_filters.$and');
  if (!Array.isArray(conditions)) throw new Error('Previous run has a nonrepresentable cell mapping condition.');
  const rules: Record<string, CellRuleValue> = {};
  const readCondition = (condition: Record<string, unknown>, grouped: boolean) => {
    const entries = Object.entries(condition);
    if (entries.length !== 1) throw new Error('Previous run has a nonrepresentable cell mapping predicate.');
    const [column, predicate] = entries[0];
    const probeId = columnProbeIds[column];
    const ruleValue = get(predicate, '$eq');
    if (
      !probeId ||
      !includes([0, 1], ruleValue) ||
      !isEqual(predicate, { $eq: ruleValue }) ||
      (grouped && ruleValue !== 1) ||
      rules[probeId] !== undefined
    ) {
      throw new Error('Previous run has a nonrepresentable or conflicting RNA mapping predicate.');
    }
    rules[probeId] = grouped ? CellRuleValue.OneOfTwoMustBePositive : ruleValue;
  };
  for (const condition of conditions) {
    if (isEqual(Object.keys(condition), ['cell.label'])) continue;
    if (Array.isArray(condition.$or) && Object.keys(condition).length === 1) {
      condition.$or.forEach((entry: Record<string, unknown>) => readCondition(entry, true));
    } else readCondition(condition, false);
  }
  if (isEmpty(rules)) throw new Error('Previous run has an unconstrained RNA mapping.');
  return rules;
};

export const restoreCellTypingExecution = (
  job: { id?: string; name?: string; type?: JobType; status?: string; deletedAt?: string | null; params?: unknown },
  studyId: string,
  panels: MultiplexPanelView[],
  cellTypes: CellType[]
): RestoredCellTypingExecution => {
  const params = job.params as CalculateFeaturesParams | undefined;
  if (
    !job.id ||
    job.type !== JobType.PostProcessingAndCalculateFeatures ||
    job.status !== JobStatus.Completed ||
    job.deletedAt
  ) {
    throw new Error('Choose a completed, active post-processing run.');
  }
  if (params?.studyId !== studyId) throw new Error('Previous run does not belong to this study.');
  if (isEmpty(panels)) throw new Error('Select at least one panel target before restoring a previous run.');
  if (!params.inputs || !params.manifest || !Array.isArray(params.postprocessing) || !Array.isArray(params.features)) {
    throw new Error('Previous run is missing its frozen recipe or input references.');
  }
  const frozenParams = cloneDeep(params);
  const sourceSlideIds = uniq(flatMap(values(params.manifest)));
  const inputsBySlideId: Record<string, ResultsInput[]> = {};
  forEach(params.inputs, (caseInputs) =>
    forEach(caseInputs, (entries, slideId) => {
      inputsBySlideId[slideId] = [...(inputsBySlideId[slideId] || []), ...entries];
    })
  );
  const panelDrafts = fromPairs(
    map(panels, (panel) => {
      const draft = createPanelConfigurationDraft(panel);
      const channels = flatMap(panel.channels, (channel) => (channel.saved ? [channel.saved] : []));
      if (channels.length !== panel.channels.length || !panel.saved)
        throw new Error('Define the current panel before restoring its run.');
      const memberIds = map(panel.slides, 'slideId');
      if (!every(memberIds, (slideId) => includes(sourceSlideIds, slideId))) {
        throw new Error('Previous run does not cover every slide in the current panel scope.');
      }
      const inputs = flatMap(memberIds, (slideId) => inputsBySlideId[slideId] ?? []);
      if (isEmpty(inputs) || !every(inputs, (entry) => Boolean(entry.input?.artifactUrl))) {
        throw new Error('Previous run is missing exact input artifact references.');
      }
      const countInputs = filter(
        map(inputs, (entry) => ({ ...entry.input, ...entry.override })),
        (input) => Boolean(input.countBins || input.binning)
      );
      const setups = map(countInputs, (input) => {
        const setup = getSpotDetectionSetupFromInput(input);
        if (
          (input.segmentationDilationsUm && !isEqual(input.segmentationDilationsUm, setup.segmentationDilationsUm)) ||
          (input.countBins &&
            !isEqual(
              map(input.countBins, (bin) => [bin.minCount, bin.maxCount]),
              map(setup.countBins, (bin) => [bin.minCount, bin.maxCount])
            ))
        ) {
          throw new Error('Previous run has count settings that cannot be restored without changing them.');
        }
        return setup;
      });
      if (!isEmpty(setups)) {
        if (!every(setups, (setup) => isEqual(setup, setups[0])))
          throw new Error('This panel has different counting settings in the previous run; restore separate groups.');
        const { perProbeCountBins, ...setup } = setups[0];
        draft.spotDetectionSetup = { ...draft.spotDetectionSetup, ...setup };
        draft.perProbeSetup = fromPairs(map(perProbeCountBins, (countBins, probeId) => [probeId, { countBins }]));
      }
      const probes = filter(channels, { channelModality: 'rna' });
      const markers = filter(channels, { channelModality: 'mif' });
      draft.probeThresholdLinks = fromPairs(map(probes, (probe) => [probe.analysisName, false]));
      const positivity = filter(params.postprocessing, {
        actionName: 'get_instance_count_positivity_by_thresholding',
        stain: 'mplex',
      });
      if (positivity.length > 1)
        throw new Error('Previous run has multiple positivity definitions; restore separate groups.');
      const thresholds = get(positivity[0], 'action_params.thresholds_by_instance', {});
      draft.probeThresholds = fromPairs(
        map(probes, (probe) => {
          const threshold = thresholds[probe.analysisName];
          if (positivity.length && (typeof threshold !== 'number' || !Number.isFinite(threshold) || threshold < 0))
            throw new Error('Previous run is missing a valid positivity cutoff for this panel.');
          return [probe.analysisName, threshold === undefined ? '' : String(threshold)];
        })
      );
      draft.markerThresholds = fromPairs(
        map(markers, (marker) => [
          marker.analysisName,
          params.pipsStudyInputs?.perMarkerThresholds?.[marker.analysisName] === undefined
            ? ''
            : String(params.pipsStudyInputs.perMarkerThresholds[marker.analysisName]),
        ])
      );
      const ruleRows: Record<string, Record<string, CellRuleValue>> = {};
      if (!isEmpty(markers)) {
        for (const [shortId, rules] of Object.entries(params.pipsStudyInputs?.cellRules ?? {})) {
          if (
            !every(
              rules,
              (value, markerId) =>
                includes(map(markers, 'analysisName'), markerId) &&
                typeof value === 'number' &&
                includes([-1, 0, 1, 2], value)
            )
          )
            throw new Error('Previous run has cell rules outside this panel; restore separate groups.');
          ruleRows[resolveCellType(shortId, cellTypes)] = { ...rules };
        }
      }
      const columnProbeIds = fromPairs(
        flatMap(probes, (probe) =>
          map(getSpotDetectionCountColumns(probe.analysisName, draft.spotDetectionSetup), (column) => [
            `${column.columnName}.pos`,
            probe.analysisName,
          ])
        )
      );
      const mappedTargets = new Set<string>();
      for (const action of filter(params.postprocessing, { actionName: 'map_cells', stain: 'mplex' })) {
        if (isUndefinedCellSink(action)) continue;
        const cellTypeId = resolveCellType(action.output_name, cellTypes);
        if (mappedTargets.has(cellTypeId))
          throw new Error('Previous run has repeated ordered mappings for one class; use the full action editor.');
        mappedTargets.add(cellTypeId);
        ruleRows[cellTypeId] = { ...ruleRows[cellTypeId], ...parseRnaMapping(action, columnProbeIds) };
      }
      draft.cellTypeIds = Object.keys(ruleRows);
      if (
        !isEqual(
          filter(draft.cellTypeIds, (cellTypeId) => mappedTargets.has(cellTypeId)),
          Array.from(mappedTargets)
        )
      ) {
        throw new Error('Previous run has different mIF and RNA rule orders; use the full action editor.');
      }
      draft.cellRules = flatMap(ruleRows, (rules, cellTypeId) =>
        map(rules, (ruleValue, stainTypeId): MinimalCellRule => ({ cellTypeId, stainTypeId, ruleValue }))
      );
      return [panel.panelId, draft];
    })
  );
  return {
    id: `execution-${job.id}`,
    sourceJobId: job.id,
    label: job.name || 'Previous execution',
    kind: 'execution',
    panelDrafts,
    frozenParams,
    requestedOutputsByPanel: fromPairs(
      map(panels, (panel) => [
        panel.panelId,
        {
          positivity: params.postprocessing.some(
            (action) => action.actionName === 'get_instance_count_positivity_by_thresholding'
          ),
          cellTypes: params.postprocessing.some(
            (action) => action.actionName === 'map_cells' || action.actionName === 'get_cell_types_by_knn'
          ),
          hScores: params.features.some((action) => action.actionName === 'instance_h_score'),
        },
      ])
    ),
  };
};
